/**
 * PatientMergeModal.tsx
 *
 * Faithful v5 port of the legacy patient_merge.php / patient-merge-test.php
 * "Choose Patient to Keep" flow. The user opens this modal from the
 * Demographics tab of a patient detail page (the "origin" patient), then
 * searches for the duplicate and picks which side to keep via one of two
 * `Keep This Patient` buttons — exactly mirroring legacy lines 336 and 491.
 *
 * Payload contract on POST /api/v5/patients/merge matches legacy verbatim:
 *   patientID : duplicate candidate (the one clicked in search)
 *   merge     : origin patient (the one whose page opened the modal)
 *   keep      : 'patient' = keep patientID, 'merge' = keep merge
 *
 * Props:
 *   isOpen            – modal visibility
 *   onClose           – called when the modal should close (cancel / backdrop)
 *   primaryPatientId  – ID of the patient whose details opened the modal
 *                       (i.e. `merge` on the wire)
 *   onMerged          – called with the kept patient ID after a successful merge
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import { toast } from '@/store/toastStore';
import { getPatient, mergePatients, type PatientRecord } from '@/services/patients.service';
import { getPlansByPatient } from '@/services/plans.service';
import type { PatientSummary } from '@/types/patient.types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // v4 returns birthDate as 'YYYY-MM-DD' (or sometimes '0000-00-00')
  if (raw === '0000-00-00' || raw === '0000-00-00 00:00:00') return null;
  const d = new Date(raw + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function computeAge(raw: string | null | undefined): string | null {
  if (!raw || raw === '0000-00-00' || raw === '0000-00-00 00:00:00') return null;
  const d = new Date(raw + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age >= 0 ? String(age) : null;
}

function formatPhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/[^0-9]/g, '');
  if (digits.length === 7) return digits.replace(/(\d{3})(\d{4})/, '$1-$2');
  if (digits.length === 10) return digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  if (digits.length === 11) return digits.replace(/(\d)(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3-$4');
  return raw;
}

/**
 * Runtime shape returned by `GET /patients/{id}` and rendered into a side
 * card. The keys here are the *actual* v4 column names that the v5 API
 * passes through verbatim in PatientsController::formatPatientWithAddon().
 * The compile-time `PatientRecord` type spreads these via `...patient` so
 * the unknown raw fields exist at runtime but are not declared on the type.
 */
interface SidePatient {
  patientId: number;
  displayId: string | null;
  firstName: string;
  middleName: string | null;
  lastName: string;
  maidenName: string | null;
  gender: string | null;
  birthDate: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  homePhone: string | null;
  cellPhone: string | null;
  planCount: number | null;
}

function toSidePatient(raw: PatientRecord, planCount: number | null): SidePatient {
  // `raw` carries every field that PatientsController::formatPatientWithAddon
  // emitted, but the TS shape on PatientRecord only declares a subset. Cast
  // through `unknown` to read the raw v4 columns directly.
  const r = raw as unknown as Record<string, unknown>;
  const s = (v: unknown): string | null => {
    if (v === null || v === undefined) return null;
    const out = String(v);
    return out === '' ? null : out;
  };
  return {
    patientId:   raw.patientId,
    displayId:   s(r.displayId ?? r.displayID),
    firstName:   raw.firstName,
    middleName:  s(r.middleName),
    lastName:    raw.lastName,
    maidenName:  s(r.maidenName),
    gender:      s(r.gender),
    birthDate:   s(r.birthDate ?? r.dateOfBirth),
    email:       s(r.email),
    address:     s(r.address),
    city:        s(r.city),
    state:       s(r.state),
    zip:         s(r.zip),
    homePhone:   s(r.homePhone),
    cellPhone:   s(r.cellPhone),
    planCount,
  };
}

interface Row {
  label: string;
  value: string | null;
}

function buildRows(p: SidePatient | null): Row[] {
  if (!p) return [];
  return [
    { label: 'ID #',           value: p.displayId },
    { label: 'First Name',     value: p.firstName || null },
    { label: 'Middle Name',    value: p.middleName },
    { label: 'Last Name',      value: p.lastName || null },
    { label: 'Maiden Name',    value: p.maidenName },
    { label: 'Gender',         value: p.gender },
    { label: 'Birth Date',     value: formatDate(p.birthDate) },
    { label: 'Age',            value: computeAge(p.birthDate) },
    { label: 'E-mail',         value: p.email },
    { label: 'Street Address', value: p.address },
    { label: 'City',           value: p.city },
    { label: 'State',          value: p.state },
    { label: 'Zip',            value: p.zip },
    { label: 'Home Phone',     value: formatPhone(p.homePhone) },
    { label: 'Cell Phone',     value: formatPhone(p.cellPhone) },
    { label: 'Plans',          value: typeof p.planCount === 'number' ? String(p.planCount) : null },
  ].filter((r) => r.value !== null && r.value !== '');
}

// ─── Side card ────────────────────────────────────────────────────────────────

interface SideCardProps {
  title: string;
  patient: SidePatient | null;
  loading: boolean;
  highlighted: boolean;
  onHover: () => void;
  onLeave: () => void;
  onKeep: () => void;
  disabled: boolean;
  busy: boolean;
}

function SideCard({ title, patient, loading, highlighted, onHover, onLeave, onKeep, disabled, busy }: SideCardProps) {
  const rows = buildRows(patient);
  return (
    <div
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      className={
        'flex flex-col rounded-lg border p-4 transition-colors ' +
        (highlighted ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white')
      }
    >
      <div className="mb-2 text-center">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</span>
      </div>
      <div className="mb-3 text-center">
        <Button
          type="button"
          size="sm"
          variant="primary"
          onClick={onKeep}
          disabled={disabled}
          loading={busy}
        >
          <Check className="mr-1.5 h-3.5 w-3.5" />
          Keep This Patient
        </Button>
      </div>
      {loading ? (
        <div className="py-8 text-center">
          <Spinner />
        </div>
      ) : patient ? (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-slate-100 last:border-0">
                <td className="py-1.5 pr-3 text-right text-xs font-semibold text-slate-500 whitespace-nowrap w-1/3">
                  {r.label}
                </td>
                <td className="py-1.5 text-sm text-slate-800 break-words">{r.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="py-8 text-center text-sm text-slate-400 italic">
          No patient selected
        </div>
      )}
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface PatientMergeModalProps {
  isOpen: boolean;
  onClose: () => void;
  primaryPatientId: number;
  onMerged?: (keptPatientId: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

async function loadSidePatient(patientId: number): Promise<SidePatient> {
  const [record, plans] = await Promise.all([
    getPatient(patientId),
    getPlansByPatient(patientId).catch(() => []),
  ]);
  return toSidePatient(record, plans.length);
}

export function PatientMergeModal({
  isOpen,
  onClose,
  primaryPatientId,
  onMerged,
}: PatientMergeModalProps) {
  const [primaryPatient, setPrimaryPatient] = useState<SidePatient | null>(null);
  const [selectedSummary, setSelectedSummary] = useState<PatientSummary | null>(null);
  const [duplicatePatient, setDuplicatePatient] = useState<SidePatient | null>(null);
  const [loadingPrimary, setLoadingPrimary] = useState(false);
  const [loadingDuplicate, setLoadingDuplicate] = useState(false);
  const [merging, setMerging] = useState(false);
  const [hovered, setHovered] = useState<'patient' | 'merge' | null>(null);

  // Load the origin patient when the modal opens
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setLoadingPrimary(true);
    setPrimaryPatient(null);
    setSelectedSummary(null);
    setDuplicatePatient(null);
    setHovered(null);

    loadSidePatient(primaryPatientId)
      .then((p) => {
        if (!cancelled) setPrimaryPatient(p);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Could not load patient', 'Please close and try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPrimary(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, primaryPatientId]);

  // Load the full duplicate patient record when one is picked from search
  useEffect(() => {
    if (!selectedSummary) {
      setDuplicatePatient(null);
      return;
    }
    let cancelled = false;
    setLoadingDuplicate(true);
    setDuplicatePatient(null);

    loadSidePatient(selectedSummary.patientId)
      .then((p) => {
        if (!cancelled) setDuplicatePatient(p);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Could not load patient', 'Please pick a different patient.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDuplicate(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedSummary]);

  const runMerge = async (keep: 'patient' | 'merge') => {
    if (!selectedSummary || merging) return;

    // Verbatim legacy confirm text (patient-merge-test.php line 255).
    const ok = window.confirm(
      'The other patient record will be deleted and all plans, payments,\n' +
        'appointments & notes will be transferred to this patient.\n' +
        'This cannot be undone.  Do you want to continue?',
    );
    if (!ok) return;

    setMerging(true);
    try {
      const result = await mergePatients({
        patientID: selectedSummary.patientId,
        merge: primaryPatientId,
        keep,
      });

      const keptName =
        result.keptPatientId === primaryPatientId
          ? `${primaryPatient?.firstName ?? ''} ${primaryPatient?.lastName ?? ''}`.trim()
          : duplicatePatient
            ? `${duplicatePatient.firstName ?? ''} ${duplicatePatient.lastName ?? ''}`.trim()
            : selectedSummary.displayName;

      toast.success(
        'Patients merged',
        keptName ? `Kept ${keptName}.` : 'The merge completed successfully.',
      );

      onMerged?.(result.keptPatientId);
      onClose();
    } catch {
      toast.error('Merge failed', 'An error occurred. Please try again.');
    } finally {
      setMerging(false);
    }
  };

  const ready = !!primaryPatient && !!duplicatePatient && !merging;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Merge Patients"
      size="xl"
      footer={
        <Button variant="secondary" onClick={onClose} disabled={merging}>
          Cancel
        </Button>
      }
      closeOnBackdrop={!merging}
    >
      {loadingPrimary ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Warning banner */}
          <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
            <p className="text-sm text-amber-800">
              <strong>This cannot be undone.</strong> All plans, payments,
              appointments, notes, tags, memberships, packages, gift cards,
              tasks, comm log, documents and other records from the other
              patient will be transferred to the one you choose to keep, and
              the other record will be deleted.
            </p>
          </div>

          {/* Search */}
          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">
              Search for the duplicate patient
            </p>
            <PatientSearchInput
              onSelect={setSelectedSummary}
              selectedPatient={selectedSummary}
              placeholder="Search by name, email, or phone…"
              excludePatientId={primaryPatientId}
            />
          </div>

          {/* Side-by-side */}
          {selectedSummary && (
            <div>
              <p className="mb-3 text-center text-sm font-medium text-slate-700">
                Choose Patient to Keep
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Left card = duplicate candidate, "patient" on the wire */}
                <SideCard
                  title="Duplicate candidate"
                  patient={duplicatePatient}
                  loading={loadingDuplicate}
                  highlighted={hovered === 'patient'}
                  onHover={() => setHovered('patient')}
                  onLeave={() => setHovered(null)}
                  onKeep={() => void runMerge('patient')}
                  disabled={!ready}
                  busy={merging && hovered === 'patient'}
                />
                {/* Right card = origin patient, "merge" on the wire */}
                <SideCard
                  title="Current patient"
                  patient={primaryPatient}
                  loading={false}
                  highlighted={hovered === 'merge'}
                  onHover={() => setHovered('merge')}
                  onLeave={() => setHovered(null)}
                  onKeep={() => void runMerge('merge')}
                  disabled={!ready}
                  busy={merging && hovered === 'merge'}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
